import React, { useMemo, useState } from "react";
import { useGetCustomerEventsQuery } from "../../../app/api/console/consoleCustomerAnalyticsApiSlice";
import { Card, EmptyState, EventBadge, Loading, formatTime } from "./ui";
import { filterEvents, uniqueEventTypes, uniqueUsers } from "./helpers";

const INSTRUMENTED_EVENTS = [
  ["finding.marked_done", "User marks finding done", "Lifecycle"],
  ["finding.discarded", "User discards finding (with reason)", "Lifecycle"],
  ["finding.shared", "User shares to Linear / Slack / Email / PDF", "Lifecycle"],
  ["finding.assigned", "User claims a finding (I'm on it) or assigns it", "Lifecycle"],
  ["finding.drilled_in", "User opens finding detail", "Lifecycle"],
  ["finding.example_expanded", "User expands a grouped example", "Lifecycle"],
  ["finding.external_url_clicked", "Click on the actual customer-page link", "Lifecycle"],
  ["finding.time_spent_ms", "Dwell time on detail page", "Engagement"],
  ["finding.drag_reordered", "User drag-reorders the ranking", "Ranking"],
  ["ranking.viewed", "User loads the top-10 home", "Ranking"],
  ["ranking.position_clicked", "User clicks a top-10 position", "Ranking"],
  ["ranking.reordered", "System or user re-orders the ranking", "Ranking"],
  ["activity.run_clicked", "User clicks a row in the Activity feed", "Activity"],
  ["journey.session_start / navigation / session_end", "Session boundaries and page-to-page moves", "Journey"],
  ["release_announcement.*", "What's-new dialog viewed / dismissed / blog / video", "Announcements"],
  ["notification.run_announcement_sent", "Server-side mirror of a run-complete email (never counts as activity)", "Emails"],
];

const select = "px-3 py-1.5 text-body-xs border border-border-default rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-accent-purple/20 cursor-pointer";

// The most recent captured events with filters, plus the instrumentation reference.
export default function FeedTab({ timeRange, useProd, websites, onInspect }) {
  const { data: events = [], isLoading } = useGetCustomerEventsQuery({ limit: 100, timeRange, useProd });
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [user, setUser] = useState("all");
  const [website, setWebsite] = useState("all");
  const types = useMemo(() => uniqueEventTypes(events), [events]);
  const users = useMemo(() => uniqueUsers(events), [events]);
  const rows = useMemo(() => filterEvents(events, { type, user, website, search }), [events, type, user, website, search]);
  const env = useProd ? "production" : "staging";

  return (
    <div className='space-y-6' data-testid='tab-feed'>
      <Card className='space-y-4'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-border-subtle pb-3'>
          <div>
            <h3 className='text-body-sm font-bold text-text-primary'>Live {env} feed</h3>
            <p className='text-caption text-text-tertiary mt-0.5'>The 100 most recent captured events in {env} within the page range.</p>
          </div>
          <div className='flex flex-wrap items-center gap-3'>
            <select value={website} onChange={(e) => setWebsite(e.target.value)} className={select} aria-label='Website'>
              <option value='all'>All websites</option>
              {(websites || []).map((site) => (
                <option key={site.website_id} value={site.website_id}>
                  {site.name || site.website_id}
                </option>
              ))}
            </select>
            <select value={user} onChange={(e) => setUser(e.target.value)} className={select} aria-label='User'>
              <option value='all'>All users</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.label}
                </option>
              ))}
            </select>
            <select value={type} onChange={(e) => setType(e.target.value)} className={select} aria-label='Event type'>
              <option value='all'>All events</option>
              {types.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <input
              type='search'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search actions…'
              aria-label='Search events'
              className='px-3 py-1.5 text-body-xs bg-white border border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-purple/20 placeholder:text-text-tertiary min-w-[200px]'
            />
          </div>
        </div>

        {isLoading ? (
          <Loading label='Loading events…' />
        ) : (
          <div className='border border-border-subtle rounded-xl overflow-hidden'>
            <div className='overflow-auto max-h-[640px]'>
              <table className='w-full text-left text-body-xs font-semibold'>
                <thead className='sticky top-0 bg-white z-10'>
                  <tr className='bg-surface-neutral/60 border-b border-border-subtle'>
                    <th className='px-4 py-2.5 font-bold text-text-secondary uppercase tracking-[0.04em] w-48'>Timestamp</th>
                    <th className='px-4 py-2.5 font-bold text-text-secondary uppercase tracking-[0.04em] w-56'>User / session</th>
                    <th className='px-4 py-2.5 font-bold text-text-secondary uppercase tracking-[0.04em] w-48'>Event type</th>
                    <th className='px-4 py-2.5 font-bold text-text-secondary uppercase tracking-[0.04em] w-64'>Targets</th>
                    <th className='px-4 py-2.5 font-bold text-text-secondary uppercase tracking-[0.04em]'>Properties</th>
                    <th className='px-4 py-2.5 w-12'></th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-border-subtle'>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className='px-4 py-8 text-center text-text-tertiary'>
                        No matching events in {env}.
                      </td>
                    </tr>
                  ) : (
                    rows.map((event) => {
                      const props = event.properties ? JSON.stringify(event.properties) : "";
                      return (
                        <tr key={event.id} className='hover:bg-surface-neutral/30 transition-colors'>
                          <td className='px-4 py-3 whitespace-nowrap text-text-secondary font-mono text-[11px]'>{formatTime(event.timestamp)}</td>
                          <td className='px-4 py-3'>
                            <div className='font-mono text-text-primary text-[11px] truncate max-w-[200px]' title={event.user_id}>
                              {event.user_email || event.user_id}
                            </div>
                            {event.session_id && (
                              <div className='font-mono text-text-tertiary text-[10px] truncate max-w-[200px]' title={event.session_id}>
                                {event.session_id}
                              </div>
                            )}
                          </td>
                          <td className='px-4 py-3 whitespace-nowrap'><EventBadge type={event.event_type} /></td>
                          <td className='px-4 py-3 font-mono text-[10px]'>
                            {event.finding_id && <div className='text-text-primary truncate max-w-[240px]' title={event.finding_id}>Finding: {event.finding_id}</div>}
                            {event.group_id && <div className='text-text-secondary truncate max-w-[240px]' title={event.group_id}>Group: {event.group_id}</div>}
                            {event.scan_id && <div className='text-text-tertiary truncate max-w-[240px]' title={event.scan_id}>Scan: {event.scan_id}</div>}
                            {!event.finding_id && !event.group_id && !event.scan_id && <span className='text-text-tertiary'>—</span>}
                          </td>
                          <td className='px-4 py-3 max-w-xs xl:max-w-md'>
                            <div className='font-mono text-[10px] text-text-secondary truncate' title={props}>{props || "—"}</div>
                          </td>
                          <td className='px-4 py-3 whitespace-nowrap text-right'>
                            <button type='button' onClick={() => onInspect(event)} className='text-accent-purple hover:opacity-85 font-semibold text-body-xs'>
                              Inspect
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      <details className='bg-white rounded-2xl border border-border-subtle overflow-hidden group'>
        <summary className='px-5 py-4 list-none flex items-center justify-between cursor-pointer select-none bg-surface-neutral/30 hover:bg-surface-neutral/60'>
          <span className='text-body-sm font-bold text-text-primary'>
            Instrumented events <span className='text-caption font-normal text-text-tertiary'>(reference · what each event captures)</span>
          </span>
          <span className='text-caption font-bold text-accent-purple group-open:rotate-180 transition-transform'>▼</span>
        </summary>
        <div className='p-5'>
          <table className='w-full text-left text-body-xs font-semibold'>
            <thead>
              <tr className='bg-surface-neutral/60 border-b border-border-subtle'>
                <th className='px-4 py-2 font-bold text-text-secondary uppercase'>Event</th>
                <th className='px-4 py-2 font-bold text-text-secondary uppercase'>What it captures</th>
                <th className='px-4 py-2 font-bold text-text-secondary uppercase w-32'>Family</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-border-subtle'>
              {INSTRUMENTED_EVENTS.map(([name, what, family]) => (
                <tr key={name}>
                  <td className='px-4 py-2.5 font-mono text-text-primary'>{name}</td>
                  <td className='px-4 py-2.5 text-text-secondary'>{what}</td>
                  <td className='px-4 py-2.5'><span className='px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-100 text-gray-600'>{family}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
      {events.length === 0 && !isLoading && <EmptyState title='Nothing captured in this window' />}
    </div>
  );
}
